import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateMarketingContact, useCreateTrustiqPartialLead } from "@workspace/api-client-react";
import { Seo } from "@/components/Seo";
import { Section, Eyebrow } from "@/components/Section";
import { trackEvent } from "@/lib/utils";
import {
  TRUSTIQ_QUESTIONS,
  TRUSTIQ_BANDS,
  scoreTrustIQ,
  computeRiskGauge,
  type TrustIQBand,
  type TrustIQBandInfo,
  type TrustIQQuestion,
  type RiskLevel,
} from "@/lib/trustiq";
import { CheckCircle, ChevronRight, ArrowRight, Shield, AlertTriangle, FileText, Users } from "lucide-react";

const ROLES = [
  "Agency principal",
  "Head of property management",
  "Trust & accounts",
  "Compliance & risk",
  "Other",
] as const;

const TEAM_SIZES = [
  "1–5 people",
  "6–20 people",
  "21–50 people",
  "51–200 people",
  "200+ people",
] as const;

const CONCERNS = [
  "Payment-redirection fraud",
  "Regulatory / compliance exposure",
  "Reputational risk from a fraud incident",
  "Staff awareness and training gaps",
  "Insurance coverage gaps",
  "Other",
] as const;

const EARLY_ACCESS_OPTIONS = [
  { value: "updates_only", label: "Keep me updated (no sales calls)" },
  { value: "demo", label: "I'd like a product demo" },
  { value: "pilot", label: "I'm interested in a pilot discussion" },
] as const;

type Stage = "hero" | "assessment" | "unlock" | "result" | "confirmed";

const UnlockSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  email: z.string().email("Please enter a valid work email"),
  role: z.enum(["Agency principal", "Head of property management", "Trust & accounts", "Compliance & risk", "Other"]),
});
type UnlockValues = z.infer<typeof UnlockSchema>;

const EarlyAccessSchema = z.object({
  agencyName: z.string().min(1, "Required"),
  teamSize: z.string().optional(),
  concern: z.string().optional(),
  earlyAccessInterest: z.enum(["updates_only", "demo", "pilot"]),
  marketingConsent: z.literal(true, { errorMap: () => ({ message: "Please tick to continue" }) }),
});
type EarlyAccessValues = z.infer<typeof EarlyAccessSchema>;

const RISK_LEVEL_ICONS: Record<RiskLevel, string> = {
  Low: "▲",
  Elevated: "▲▲",
  High: "▲▲▲",
  Critical: "▲▲▲▲",
};

function RiskGauge({ answers }: { answers: Record<string, number> }) {
  const gauge = computeRiskGauge(answers);
  const levels: RiskLevel[] = ["Low", "Elevated", "High", "Critical"];
  const activeIdx = levels.indexOf(gauge.level);

  const segmentColors: Record<RiskLevel, string> = {
    Low: "hsl(168, 35%, 42%)",
    Elevated: "hsl(42, 70%, 44%)",
    High: "hsl(25, 80%, 46%)",
    Critical: "hsl(0, 65%, 48%)",
  };

  return (
    <div className="rounded-lg border px-4 py-3 flex flex-col gap-2 transition-colors duration-500"
      style={{ borderColor: gauge.borderColor, backgroundColor: gauge.bgColor }}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-mono uppercase tracking-wider" style={{ color: gauge.color }}>
          Risk level
        </span>
        <span className="text-xs font-semibold tabular-nums transition-all duration-500" style={{ color: gauge.color }}>
          {gauge.level}
        </span>
      </div>

      <div className="flex gap-1">
        {levels.map((level, i) => (
          <div
            key={level}
            className="h-1.5 flex-1 rounded-full transition-all duration-500"
            style={{
              backgroundColor: i <= activeIdx ? segmentColors[level] : "hsl(var(--brand-sand))",
              opacity: i <= activeIdx ? 1 : 0.4,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  return (
    <div className="w-full bg-[hsl(var(--brand-sand))] rounded-full h-1.5">
      <div
        className="bg-[hsl(var(--brand-forest))] h-1.5 rounded-full transition-all duration-500"
        style={{ width: `${(current / total) * 100}%` }}
      />
    </div>
  );
}

function BandPill({ band, bandInfo, className }: { band: TrustIQBand; bandInfo: TrustIQBandInfo; className?: string }) {
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${className ?? ""}`}
      style={{ color: bandInfo.color, backgroundColor: bandInfo.bgColor }}
    >
      {band}
    </span>
  );
}

function AnimatedScoreDisplay({ score, band, bandInfo }: { score: number; band: TrustIQBand; bandInfo: TrustIQBandInfo }) {
  const [displayed, setDisplayed] = useState(0);
  const [bandVisible, setBandVisible] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    setDisplayed(0);
    setBandVisible(false);
    const duration = 1500;
    const start = performance.now();

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(eased * score));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplayed(score);
        setTimeout(() => setBandVisible(true), 120);
      }
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [score]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="w-28 h-28 rounded-full flex flex-col items-center justify-center border-4 shadow-sm"
        style={{ borderColor: bandInfo.color, backgroundColor: bandInfo.bgColor }}
      >
        <div className="text-3xl font-bold tabular-nums" style={{ color: bandInfo.color }}>{displayed}</div>
        <div className="text-xs text-[hsl(var(--brand-slate))] font-medium">/ 16</div>
      </div>
      <div className={`transition-opacity duration-400 ${bandVisible ? "opacity-100" : "opacity-0"}`}>
        <BandPill band={band} bandInfo={bandInfo} className={bandVisible ? "trustiq-band-fade-in" : ""} />
      </div>
    </div>
  );
}

export default function TrustIQ() {
  const [stage, setStage] = useState<Stage>("hero");
  const [unlockData, setUnlockData] = useState<UnlockValues | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scoreResult, setScoreResult] = useState<ReturnType<typeof scoreTrustIQ> | null>(null);
  const [feedbackText, setFeedbackText] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState<number | null>(null);

  const unlockForm = useForm<UnlockValues>({
    resolver: zodResolver(UnlockSchema),
    defaultValues: { firstName: "", lastName: "", email: "", role: "Agency principal" },
  });

  const earlyAccessForm = useForm<EarlyAccessValues>({
    resolver: zodResolver(EarlyAccessSchema),
    defaultValues: { agencyName: "", teamSize: "", concern: "", earlyAccessInterest: "updates_only" },
  });

  const mutation = useCreateMarketingContact();
  const partialLeadMutation = useCreateTrustiqPartialLead();

  const handleAnswer = (questionId: string, value: number) => {
    if (feedbackText !== null) return;

    const updated = { ...answers, [questionId]: value };
    setAnswers(updated);
    setSelectedValue(value);

    const q = TRUSTIQ_QUESTIONS.find((q) => q.id === questionId)!;
    setFeedbackText(q.feedback[value as 0 | 1 | 2]);
  };

  const handleNext = () => {
    const isLast = currentQuestion >= TRUSTIQ_QUESTIONS.length - 1;
    setFeedbackText(null);
    setSelectedValue(null);
    if (!isLast) {
      setCurrentQuestion((q) => q + 1);
    } else {
      const result = scoreTrustIQ(answers);
      setScoreResult(result);
      setStage("unlock");
      trackEvent("trustiq_assessment_completed", { score: result.score, band: result.band });
    }
  };

  const handleUnlockSubmit = (values: UnlockValues) => {
    setUnlockData(values);
    setStage("result");
    trackEvent("trustiq_unlocked", { role: values.role });

    if (scoreResult) {
      partialLeadMutation.mutate({
        data: {
          fullName: `${values.firstName} ${values.lastName}`,
          email: values.email,
          role: values.role,
          trustiqScore: scoreResult.score,
          trustiqBand: scoreResult.band,
          trustiqAnswers: answers,
        },
      });
    }
  };

  const handleEarlyAccessSubmit = (values: EarlyAccessValues) => {
    if (!unlockData || !scoreResult) return;

    const honeypot =
      (document.querySelector<HTMLInputElement>('input[name="website"]')?.value ?? "");

    mutation.mutate(
      {
        data: {
          fullName: `${unlockData.firstName} ${unlockData.lastName}`,
          email: unlockData.email,
          agencyName: values.agencyName || null,
          topic: "trustiq_assessment",
          message: `TrustIQ Assessment — Score: ${scoreResult.score}/16 (${scoreResult.band}). Role: ${unlockData.role}. Early access: ${values.earlyAccessInterest}.`,
          website: honeypot || null,
          role: unlockData.role,
          trustiqScore: scoreResult.score,
          trustiqBand: scoreResult.band,
          trustiqAnswers: answers,
          teamSize: values.teamSize || null,
          concern: values.concern || null,
          earlyAccessInterest: values.earlyAccessInterest,
        },
      },
      {
        onSuccess: () => {
          trackEvent("trustiq_registered", { band: scoreResult.band, earlyAccessInterest: values.earlyAccessInterest });
          setStage("confirmed");
        },
        onError: () => {
          earlyAccessForm.setError("root", { message: "We couldn't submit your details. Please try again." });
        },
      },
    );
  };

  const startAssessment = () => {
    setStage("assessment");
    setAnswers({});
    setCurrentQuestion(0);
    setScoreResult(null);
    setUnlockData(null);
    setFeedbackText(null);
    setSelectedValue(null);
    unlockForm.reset();
    earlyAccessForm.reset();
  };

  const q = TRUSTIQ_QUESTIONS[currentQuestion];

  return (
    <>
      <Seo
        title="TrustIQ — Free Fraud-Readiness Assessment for Real Estate Teams"
        description="Take our 8-question TrustIQ assessment and find out how vulnerable your agency is to payment-redirection fraud. Immediate result. No technical knowledge needed."
        path="/trustiq"
      />

      {/* Hero */}
      {stage === "hero" && (
        <div className="bg-[hsl(var(--brand-forest))] text-white">
          <div className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
            <div className="max-w-3xl">
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-brass))]">
                Free fraud-readiness assessment for real estate teams
              </div>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
                Your inbox is not proof of identity.
              </h1>
              <p className="mt-6 text-lg text-[hsl(var(--brand-bone))/70] max-w-2xl leading-relaxed">
                Australian real estate agencies lose hundreds of thousands of dollars each year to payment-redirection fraud — and most don't know they were vulnerable until it's too late. Take our 5-minute assessment and find out exactly where your gaps are, with a personalised action plan.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-[hsl(var(--brand-slate))] font-mono tracking-wide">
                <span>8 questions</span>
                <span className="text-[hsl(var(--brand-brass))]">·</span>
                <span>Immediate result</span>
                <span className="text-[hsl(var(--brand-brass))]">·</span>
                <span>No technical knowledge needed</span>
              </div>
              <div className="mt-10">
                <button
                  onClick={startAssessment}
                  className="inline-flex items-center gap-2 bg-[hsl(var(--brand-brass))] text-[hsl(var(--brand-charcoal))] px-8 py-4 rounded font-semibold text-lg hover:bg-[hsl(42,55%,52%)] transition-colors"
                  data-testid="button-start-trustiq"
                >
                  Start assessment <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment */}
      {stage === "assessment" && q && (
        <div className="min-h-[60vh] flex items-start">
          <Section className="w-full !pt-12">
            <div className="max-w-2xl mx-auto">
              <div className="mb-4 flex items-center justify-between gap-4">
                <span className="text-sm text-[hsl(var(--brand-slate))] font-mono">
                  Question {currentQuestion + 1} of {TRUSTIQ_QUESTIONS.length}
                </span>
                <span className="text-sm text-[hsl(var(--brand-slate))] font-mono">
                  TrustIQ Assessment
                </span>
              </div>
              <ProgressBar current={currentQuestion + 1} total={TRUSTIQ_QUESTIONS.length} />

              <div className="mt-4">
                <RiskGauge answers={answers} />
              </div>

              <div key={currentQuestion} className="mt-8 trustiq-question-in">
                <h2 className="text-xl sm:text-2xl font-semibold text-[hsl(var(--brand-charcoal))] leading-snug">
                  {q.question}
                </h2>

                <div className="mt-8 min-h-[160px]">
                  {feedbackText !== null ? (
                    <div className="trustiq-feedback-in" data-testid="assessment-feedback">
                      <div className="flex items-start gap-3 rounded-lg border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] px-5 py-4">
                        <div
                          className="mt-0.5 w-2.5 h-2.5 rounded-full shrink-0"
                          style={{
                            backgroundColor:
                              selectedValue === 2 ? "hsl(168, 35%, 38%)" :
                              selectedValue === 1 ? "hsl(42, 70%, 44%)" :
                              "hsl(0, 65%, 48%)",
                          }}
                        />
                        <p className="text-sm leading-relaxed text-[hsl(var(--brand-charcoal))]">
                          {feedbackText}
                        </p>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={handleNext}
                          className="inline-flex items-center gap-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-5 py-2.5 rounded font-medium text-sm hover:bg-[hsl(168,25%,22%)] transition-colors"
                        >
                          {currentQuestion >= TRUSTIQ_QUESTIONS.length - 1 ? "See my result" : "Next question"}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3" data-testid="assessment-options">
                      {q.options.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => handleAnswer(q.id, opt.value)}
                          className={`w-full text-left px-5 py-4 rounded border text-sm leading-relaxed transition-all ${
                            answers[q.id] === opt.value
                              ? "border-[hsl(var(--brand-forest))] bg-[hsl(168,25%,96%)] text-[hsl(var(--brand-charcoal))] font-medium"
                              : "border-[hsl(var(--brand-sand))] bg-white text-[hsl(var(--brand-charcoal))] hover:border-[hsl(var(--brand-forest))]/50 hover:bg-[hsl(168,25%,99%)]"
                          }`}
                          data-testid={`option-q${currentQuestion}-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Section>
        </div>
      )}

      {/* Unlock form — shown after last question */}
      {stage === "unlock" && scoreResult && (
        <div className="min-h-[60vh] flex items-start">
          <Section className="w-full !pt-12">
            <div className="max-w-md mx-auto">
              <div className="mb-2 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[hsl(var(--brand-forest))]" />
                <span className="text-sm font-medium text-[hsl(var(--brand-forest))]">
                  Assessment complete
                </span>
              </div>
              <h2 className="text-2xl font-semibold text-[hsl(var(--brand-charcoal))] mb-2">
                Your score is ready.
              </h2>
              <p className="text-sm text-[hsl(var(--brand-slate))] mb-8 leading-relaxed">
                Enter your details to reveal your TrustIQ score and personalised recommendations.
              </p>

              <form onSubmit={unlockForm.handleSubmit(handleUnlockSubmit)} className="space-y-4" data-testid="form-trustiq-unlock">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First name" error={unlockForm.formState.errors.firstName?.message}>
                    <input
                      {...unlockForm.register("firstName")}
                      className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                      data-testid="input-trustiq-firstname"
                    />
                  </Field>
                  <Field label="Last name" error={unlockForm.formState.errors.lastName?.message}>
                    <input
                      {...unlockForm.register("lastName")}
                      className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                      data-testid="input-trustiq-lastname"
                    />
                  </Field>
                </div>
                <Field label="Work email" error={unlockForm.formState.errors.email?.message}>
                  <input
                    type="email"
                    {...unlockForm.register("email")}
                    placeholder="you@youragency.com.au"
                    className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                    data-testid="input-trustiq-email"
                  />
                </Field>
                <Field label="Your role" error={unlockForm.formState.errors.role?.message}>
                  <select
                    {...unlockForm.register("role")}
                    className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                    data-testid="select-trustiq-role"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </Field>
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[hsl(var(--brand-brass))] text-[hsl(var(--brand-charcoal))] px-6 py-3 rounded font-semibold hover:bg-[hsl(42,55%,52%)] transition-colors"
                  data-testid="button-trustiq-unlock-submit"
                >
                  See my score <ChevronRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </Section>
        </div>
      )}

      {/* Score Result */}
      {(stage === "result" || stage === "confirmed") && scoreResult && (
        <>
          {/* Score hero bar */}
          <div
            className="py-12"
            style={{ backgroundColor: scoreResult.bandInfo.bgColor }}
          >
            <div className="mx-auto max-w-6xl px-6">
              <div className="max-w-3xl">
                <Eyebrow>Your TrustIQ result</Eyebrow>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-8">
                  <AnimatedScoreDisplay
                    score={scoreResult.score}
                    band={scoreResult.band}
                    bandInfo={scoreResult.bandInfo}
                  />
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-[hsl(var(--brand-charcoal))] leading-tight">
                      {scoreResult.bandInfo.description}
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Section className="!pt-10">
            <div className="max-w-3xl">
              {/* Weakest area recommendation */}
              <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] p-6 mb-8">
                <div className="flex gap-3 items-start">
                  <AlertTriangle className="w-5 h-5 text-[hsl(35,85%,45%)] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono tracking-wider uppercase text-[hsl(var(--brand-slate))] mb-1">
                      Your biggest gap
                    </div>
                    <h3 className="font-semibold text-[hsl(var(--brand-charcoal))] mb-2 text-sm">
                      {scoreResult.weakestQuestion.question}
                    </h3>
                    <p className="text-sm text-[hsl(var(--brand-slate))] leading-relaxed">
                      {scoreResult.weakestQuestion.recommendation}
                    </p>
                  </div>
                </div>
              </div>

              {/* Product proof paragraph */}
              <div className="rounded-lg border border-[hsl(168,25%,85%)] bg-[hsl(168,25%,97%)] p-6 mb-10">
                <div className="flex gap-3 items-start">
                  <Shield className="w-5 h-5 text-[hsl(var(--brand-forest))] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono tracking-wider uppercase text-[hsl(var(--brand-slate))] mb-1">
                      How FortifyID closes this gap
                    </div>
                    <p className="text-sm text-[hsl(var(--brand-slate))] leading-relaxed">
                      FortifyID replaces unverified email-based change requests with a structured, auditable workflow. Clients verify their identity through ConnectID® — backed by their bank — before submitting sensitive details. Every request is time-stamped, immutable, and linked to a verified identity. Your team gets a single place to review, approve, and audit every change, with mandatory segregation of duties built in. The result: a control environment that satisfies insurers, withstands scrutiny, and stops fraud before it starts.
                    </p>
                  </div>
                </div>
              </div>

              {/* Score breakdown table */}
              <div className="mb-10">
                <h3 className="text-sm font-semibold text-[hsl(var(--brand-charcoal))] mb-3">Score breakdown</h3>
                <div className="rounded border border-[hsl(var(--brand-sand))] overflow-hidden">
                  {TRUSTIQ_QUESTIONS.map((q, i) => {
                    const score = answers[q.id] ?? 0;
                    const maxScore = 2;
                    return (
                      <div
                        key={q.id}
                        className={`flex items-center gap-4 px-4 py-3 text-sm ${i % 2 === 0 ? "bg-white" : "bg-[hsl(var(--brand-bone))]"}`}
                      >
                        <div className="flex gap-1 shrink-0">
                          {Array.from({ length: maxScore }).map((_, j) => (
                            <div
                              key={j}
                              className="w-2 h-2 rounded-full"
                              style={{
                                backgroundColor: j < score ? scoreResult.bandInfo.color : "hsl(var(--brand-sand))",
                              }}
                            />
                          ))}
                        </div>
                        <div className="text-[hsl(var(--brand-charcoal))] leading-snug flex-1 min-w-0 truncate">
                          {q.question}
                        </div>
                        <div className="text-xs font-mono text-[hsl(var(--brand-slate))] shrink-0">
                          {score}/{maxScore}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Secondary early-access form or confirmation */}
              {stage === "result" && (
                <div className="rounded-xl border border-[hsl(var(--brand-sand))] bg-white p-6 sm:p-8">
                  <h3 className="font-display text-xl font-semibold text-[hsl(var(--brand-charcoal))] mb-1">
                    Get on the early-access list
                  </h3>
                  <p className="text-sm text-[hsl(var(--brand-slate))] mb-6 leading-relaxed">
                    FortifyID is purpose-built for Australian real estate agencies. Tell us a little more about your team and we'll be in touch with early access details.
                  </p>

                  <form onSubmit={earlyAccessForm.handleSubmit(handleEarlyAccessSubmit)} className="space-y-5" data-testid="form-trustiq-register">
                    <Field label="Agency name" error={earlyAccessForm.formState.errors.agencyName?.message}>
                      <input
                        {...earlyAccessForm.register("agencyName")}
                        placeholder="e.g. Ray White Parramatta"
                        className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                        data-testid="input-trustiq-agency"
                      />
                    </Field>
                    <Field label="Team size (optional)">
                      <select
                        {...earlyAccessForm.register("teamSize")}
                        className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                        data-testid="select-trustiq-teamsize"
                      >
                        <option value="">Select…</option>
                        {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Biggest concern (optional)">
                      <select
                        {...earlyAccessForm.register("concern")}
                        className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                        data-testid="select-trustiq-concern"
                      >
                        <option value="">Select…</option>
                        {CONCERNS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                    <div>
                      <div className="text-sm font-medium text-[hsl(var(--brand-charcoal))] mb-2">
                        What are you most interested in?
                      </div>
                      <div className="flex flex-col gap-2">
                        {EARLY_ACCESS_OPTIONS.map((opt) => (
                          <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="radio"
                              value={opt.value}
                              {...earlyAccessForm.register("earlyAccessInterest")}
                              className="accent-[hsl(var(--brand-forest))]"
                              data-testid={`radio-trustiq-interest-${opt.value}`}
                            />
                            <span className="text-sm text-[hsl(var(--brand-charcoal))]">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {earlyAccessForm.formState.errors.earlyAccessInterest && (
                        <p className="mt-1 text-xs text-[hsl(var(--destructive))]">{earlyAccessForm.formState.errors.earlyAccessInterest.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          {...earlyAccessForm.register("marketingConsent")}
                          className="mt-0.5 accent-[hsl(var(--brand-forest))]"
                          data-testid="checkbox-trustiq-consent"
                        />
                        <span className="text-xs text-[hsl(var(--brand-slate))] leading-relaxed">
                          I agree to receive information about FortifyID products and services. You can unsubscribe at any time. See our{" "}
                          <a href="/legal/privacy" className="underline hover:text-[hsl(var(--brand-forest))]" target="_blank" rel="noopener">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      {earlyAccessForm.formState.errors.marketingConsent && (
                        <p className="mt-1 text-xs text-[hsl(var(--destructive))]">{earlyAccessForm.formState.errors.marketingConsent.message}</p>
                      )}
                    </div>
                    {earlyAccessForm.formState.errors.root && (
                      <p className="text-sm text-[hsl(var(--destructive))]">{earlyAccessForm.formState.errors.root.message}</p>
                    )}
                    <button
                      type="submit"
                      disabled={mutation.isPending}
                      className="w-full bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-6 py-3 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors disabled:opacity-60"
                      data-testid="button-trustiq-register-submit"
                    >
                      {mutation.isPending ? "Submitting…" : "Join early access →"}
                    </button>
                  </form>
                </div>
              )}

              {stage === "confirmed" && (
                <div className="rounded-xl border border-[hsl(168,25%,80%)] bg-[hsl(168,25%,97%)] p-8">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-[hsl(var(--brand-forest))] shrink-0 mt-0.5" />
                    <div>
                      <h2 className="font-display text-xl font-semibold text-[hsl(var(--brand-charcoal))] mb-2">
                        You're on the early-access list.
                      </h2>
                      <p className="text-sm text-[hsl(var(--brand-slate))] leading-relaxed mb-4">
                        We've received your TrustIQ score ({scoreResult.score}/16 — <strong>{scoreResult.band}</strong>) and your early-access preference. A member of the FortifyID team will be in touch based on your interest.
                      </p>
                      <p className="text-sm text-[hsl(var(--brand-slate))] leading-relaxed">
                        In the meantime, explore how FortifyID works for real estate agencies — or share this assessment with a colleague.
                      </p>
                      <div className="mt-6 flex flex-wrap gap-3">
                        <a
                          href="/industries/real-estate"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-forest))] hover:underline"
                        >
                          How FortifyID works for real estate <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Section>
        </>
      )}

      {/* Below the fold — Problem + Product sections */}
      {(stage === "hero" || stage === "confirmed") && (
        <>
          <Section className="!py-20 border-t border-[hsl(var(--brand-sand))]">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <Eyebrow>The problem</Eyebrow>
                <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] leading-tight">
                  Payment-redirection fraud is the fastest-growing financial crime in Australian real estate.
                </h2>
                <p className="mt-5 text-[hsl(var(--brand-slate))] leading-relaxed">
                  Each year, Australian agencies unknowingly redirect settlement funds, rental payments, and vendor disbursements to fraudulent accounts. The attack is simple: intercept or spoof a legitimate email, swap bank details, and wait for the transfer to clear. By the time anyone notices, the money is gone.
                </p>
                <p className="mt-4 text-[hsl(var(--brand-slate))] leading-relaxed">
                  Your instinct may be to call the client to confirm. But attackers know this — and they're ready with a fake phone number or a spoofed voicemail. The only control that works is verifying identity <em>before</em> receiving the request, not after.
                </p>
                <div className="mt-8 flex flex-col gap-5">
                  {[
                    {
                      Icon: AlertTriangle,
                      title: "Business email compromise is the entry point",
                      body: "Attackers infiltrate or spoof email threads — often the agent's own account — to redirect a single, high-value transaction. The email looks identical to the real one.",
                    },
                    {
                      Icon: FileText,
                      title: "PDF forms carry no chain of custody",
                      body: "A bank-detail PDF can be intercepted and altered between your client's inbox and yours. There is no way to prove the form you received matches the one they sent.",
                    },
                    {
                      Icon: Users,
                      title: "Staff under time pressure make the call",
                      body: "Settlement days are high-pressure. One junior staff member processing a PDF without a second check is all it takes. The fraud succeeds because process breaks down exactly when it matters most.",
                    },
                  ].map(({ Icon, title, body }) => (
                    <div key={title} className="flex gap-4">
                      <div className="w-9 h-9 rounded bg-[hsl(0_35%_45%)]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-[hsl(0_35%_45%)]" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-[hsl(var(--brand-charcoal))]">{title}</h3>
                        <p className="mt-1 text-sm text-[hsl(var(--brand-slate))] leading-relaxed">{body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Eyebrow>The solution</Eyebrow>
                <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] leading-tight">
                  FortifyID — verified change requests, built for real estate.
                </h2>
                <p className="mt-5 text-[hsl(var(--brand-slate))] leading-relaxed">
                  FortifyID replaces the email-and-PDF process with a structured, identity-verified workflow. Your staff send a secure request link to the client. The client verifies their identity using ConnectID® — Australia's bank-backed digital identity network — then submits their details directly into the platform.
                </p>
                <p className="mt-4 text-[hsl(var(--brand-slate))] leading-relaxed">
                  Every submission is tied to a verified identity, time-stamped, and immutably logged. Your team reviews, approves, and acts — with a complete audit trail that satisfies insurers and stands up to regulatory scrutiny.
                </p>
                <div className="mt-8 flex flex-col gap-4">
                  {[
                    "Clients verify with their existing bank app — no new account required.",
                    "Bank details flow directly from the client's verified session into FortifyID.",
                    "Every change is reviewed by a second person before it's actioned.",
                    "Full audit trail — who requested, who approved, when, and why.",
                    "Your agency's branding on every client touchpoint.",
                  ].map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-[hsl(var(--brand-forest))] mt-0.5 shrink-0" />
                      <p className="text-sm text-[hsl(var(--brand-slate))] leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <button
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      startAssessment();
                    }}
                    className="inline-flex items-center gap-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-6 py-3 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors"
                  >
                    {stage === "confirmed" ? "Take the assessment again" : "Start assessment"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </Section>

          {/* Band reference */}
          <Section className="!pt-0 !pb-20">
            <div className="border-t border-[hsl(var(--brand-sand))] pt-16">
              <Eyebrow>TrustIQ scoring guide</Eyebrow>
              <h2 className="mt-3 text-2xl font-semibold text-[hsl(var(--brand-charcoal))] mb-8">
                What does your score mean?
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {TRUSTIQ_BANDS.map((b) => (
                  <div
                    key={b.band}
                    className="rounded-lg border p-5"
                    style={{ borderColor: b.color + "40", backgroundColor: b.bgColor }}
                  >
                    <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: b.color }}>
                      {b.band === "Exposed" ? "0–5 pts" : b.band === "Inconsistent" ? "6–10 pts" : b.band === "Controlled" ? "11–13 pts" : "14–16 pts"}
                    </div>
                    <div className="font-semibold text-[hsl(var(--brand-charcoal))] mb-2">{b.band}</div>
                    <p className="text-xs text-[hsl(var(--brand-slate))] leading-relaxed">{b.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        </>
      )}
    </>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-[hsl(var(--brand-charcoal))] mb-1.5">{label}</div>
      {children}
      {error && <div className="mt-1 text-xs text-[hsl(var(--destructive))]">{error}</div>}
    </label>
  );
}
